import { useCallback, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import type {
	LuminaSourceClickEvent,
	LuminaSourceClickHandler,
} from '@atlassian/lumina/on-source-click';

import type { AIAnswerContextType } from '../../../../ai-answer-dialog/ai-answer-context';
import { onAIAnswerCopyButtonClicked } from '../../../../ai-answer-dialog/on-ai-answer-copy-button-clicked';
import { onAIAnswerSourceClick } from '../../../../ai-answer-dialog/on-ai-answer-source-click';
import type { SourceSmartCardType } from '../../../../ai-answer-dialog/types';
import { SainAction } from '../../../../common/controller/analytics/hooks/sain-action';
import { useSAINUsageCriteriaMet } from '../../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../../common/controller/analytics/use-analytics';
import { SAINFeatureControl } from '../../../../common/utils/sain-feature-control';

type SourceClickDeps = {
	fireAnalyticsEvent: ReturnType<typeof useAnalytics>[0];
	commonAttributes: AIAnswerContextType['commonAttributes'] | undefined;
	fireSAINUsageEvent: ReturnType<typeof useSAINUsageCriteriaMet>['fireSAINUsageEvent'];
	onAIAnswerSourceClickCallback: (() => void) | undefined;
};

/**
 * Shared implementation for the inline-Lumina source click handler. Pulled out of
 * the hook so both the stable (ref-backed) and legacy (dependency-backed) variants
 * can reuse exactly the same behaviour.
 */
const runSourceClick = (event: LuminaSourceClickEvent, deps: SourceClickDeps): void => {
	const {
		fireAnalyticsEvent,
		commonAttributes,
		fireSAINUsageEvent,
		onAIAnswerSourceClickCallback,
	} = deps;

	if (!commonAttributes) {
		if (process.env.NODE_ENV !== 'production') {
			throw new Error('useLuminaSourceClickAnalytics: missing AIAnswerContext.commonAttributes.');
		}
		return;
	}

	const { url, componentType, indexInContainer, isAuxClick, isContextMenu, isCopy } = event;

	if ((isAuxClick || isContextMenu) && !SAINFeatureControl.isNonPrimaryClickTrackingEnabled()) {
		return;
	}

	// Copy interactions reuse the existing `aiAnswerCopy clicked` event.
	if (isCopy) {
		fireAnalyticsEvent(onAIAnswerCopyButtonClicked(commonAttributes, 'inline-lumina-link-embed'));

		fireSAINUsageEvent({
			metCriteria: SainAction.COPY_CLICKED,
			commonAttributes,
		});

		onAIAnswerSourceClickCallback?.();
		return;
	}

	const sourceCardType: SourceSmartCardType | undefined =
		componentType === 'linkList'
			? 'inline-lumina-link-list'
			: componentType === 'linkEmbed'
				? 'inline-lumina-link-embed'
				: undefined;

	let hostname: string | undefined;
	try {
		hostname = new URL(url).hostname;
	} catch {
		hostname = undefined;
	}

	const sourceProduct = commonAttributes.sourceProduct ?? '';

	fireAnalyticsEvent(
		onAIAnswerSourceClick({
			// Spread first so per-click fields below override context values.
			...commonAttributes,
			sourceIndex: indexInContainer,
			hostname,
			isAuxClick,
			isContextMenu,
			sourceCardType,
			sourceProduct,
			isCitation: false,
		}),
	);

	fireSAINUsageEvent({
		metCriteria: SainAction.SOURCE_CLICKED,
		commonAttributes,
		sourceProduct,
	});

	onAIAnswerSourceClickCallback?.();
};

/**
 * Fires `aiAnswerSourceLink clicked` for inline-Lumina source surfaces
 * (`<link-list>` rows, `<smart-link>` `LinkEmbed`) rendered in a SAIN answer.
 */
export const useLuminaSourceClickAnalytics = ({
	aiAnswerDialogContext,
}: {
	aiAnswerDialogContext: AIAnswerContextType;
}): LuminaSourceClickHandler => {
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const [fireAnalyticsEvent] = useAnalytics();

	const commonAttributes = aiAnswerDialogContext?.commonAttributes;
	const onAIAnswerSourceClickCallback = aiAnswerDialogContext?.callbacks?.onAIAnswerSourceClick;

	// `commonAttributes` gets a new reference on every streamed chunk (it depends on
	// the growing answer string). When this handler lists it as a `useCallback`
	// dependency, the handler identity changes every chunk and is passed as a new
	// `onSourceClick` prop into `LuminaLayout` / the inline link list, causing it to
	// re-render on every chunk while streaming. Keeping the volatile values in a ref
	// lets the handler stay stable while still reading fresh values at click time.
	const isStreamingFlickerFixEnabled = fg('kd_link_list_streaming_flicker_fix');

	const latestDepsRef = useRef<SourceClickDeps>({
		fireAnalyticsEvent,
		commonAttributes,
		fireSAINUsageEvent,
		onAIAnswerSourceClickCallback,
	});
	latestDepsRef.current = {
		fireAnalyticsEvent,
		commonAttributes,
		fireSAINUsageEvent,
		onAIAnswerSourceClickCallback,
	};

	const stableHandler = useCallback<LuminaSourceClickHandler>(
		(event: LuminaSourceClickEvent) => runSourceClick(event, latestDepsRef.current),
		// Intentionally stable: reads the latest values via `latestDepsRef`.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	const legacyHandler = useCallback<LuminaSourceClickHandler>(
		(event: LuminaSourceClickEvent) =>
			runSourceClick(event, {
				fireAnalyticsEvent,
				commonAttributes,
				fireSAINUsageEvent,
				onAIAnswerSourceClickCallback,
			}),
		[fireAnalyticsEvent, commonAttributes, fireSAINUsageEvent, onAIAnswerSourceClickCallback],
	);

	return isStreamingFlickerFixEnabled ? stableHandler : legacyHandler;
};
